import type { CSSProperties } from 'react';
import type { Theme as UtilsTheme, ColorScheme as UtilsColorScheme } from '../../../../../utils';

type Theme = UtilsTheme;

type ColorScheme = UtilsColorScheme;

interface Styles {
    container: CSSProperties;
    header: CSSProperties;
    headerTitle: CSSProperties;
    headerSubtitle: CSSProperties;
    closeButton: CSSProperties;
    content: CSSProperties;
    giftGroup: CSSProperties;
    giftGroupTitle: CSSProperties;
    giftList: CSSProperties;
    giftItem: CSSProperties;
    giftItemSelected: CSSProperties;
    giftImage: CSSProperties;
    giftInfo: CSSProperties;
    giftName: CSSProperties;
    giftDescription: CSSProperties;
    giftQuantity: CSSProperties;
    checkbox: CSSProperties;
    emptyState: CSSProperties;
    emptyText: CSSProperties;
    loadingContainer: CSSProperties;
    listTitle: CSSProperties;
    renderSectionHeaderSelected: CSSProperties;
    renderSectionHeader: CSSProperties;
    titleSelectedSection: CSSProperties;
    titleRemainingSection: CSSProperties;
    sectionHeaderTitleSelected: CSSProperties;
    selectedGiftAction: CSSProperties;
    selectedHeader: CSSProperties;
    selectedDividerSelected: CSSProperties;
    selectedDivider: CSSProperties;
    selectedTitleContainer: CSSProperties;
    selectected: CSSProperties;
    renderItem: CSSProperties;
    bottomSheetInjectContent: CSSProperties;
    bottomSheetGiftFlastList: CSSProperties;
}

/**
 * Normalize size function (matching mobile normalizeSize)
 * On web, we use rem-based sizing instead of PixelRatio
 */
const normalizeSize = (size: number): number => size;

export const giftsSelectorWidgetContentsStyles = (theme: Theme, colorScheme?: ColorScheme): Styles => {
    const primary = theme.colors.schemes[colorScheme ?? 'light']?.primary ?? '#21409A';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- giftOption may not exist in all theme types
    const containerBorderRadius = (theme.components as any)?.giftOption?.container?.borderRadius ?? 12;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- secondary10 may not exist in all theme types
    const secondary10 = (theme.colors.secondary as any)?.secondary10 ?? '#DFF4FF';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- font may not exist in all theme types
    const fontSizeLarge = (theme.size as any)?.font?.large ?? 16;

    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            backgroundColor: '#fff',
        },
        header: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 16,
            borderBottom: '1px solid #eee',
        },
        headerTitle: {
            fontSize: 18,
            fontWeight: 'bold',
            color: '#333',
        },
        headerSubtitle: {
            fontSize: 14,
            color: '#666',
            marginTop: 4,
        },
        closeButton: {
            padding: 8,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'none',
        },
        content: {
            flex: 1,
            overflowY: 'auto',
            padding: 16,
        },
        giftGroup: {
            marginBottom: 24,
        },
        giftGroupTitle: {
            fontSize: 16,
            fontWeight: 600,
            color: '#333',
            marginBottom: 12,
        },
        giftList: {
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
        },
        giftItem: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: 12,
            borderRadius: 8,
            border: '1px solid #eee',
            backgroundColor: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        },
        giftItemSelected: {
            borderColor: '#21409a',
            backgroundColor: secondary10,
        },
        giftImage: {
            width: 64,
            height: 64,
            borderRadius: 8,
            objectFit: 'cover',
            marginRight: 12,
        },
        giftInfo: {
            flex: 1,
        },
        giftName: {
            fontSize: 14,
            fontWeight: 500,
            color: '#333',
        },
        giftDescription: {
            fontSize: 12,
            color: '#666',
            marginTop: 4,
        },
        giftQuantity: {
            fontSize: 12,
            color: '#21409a',
            marginTop: 4,
        },
        checkbox: {
            width: 20,
            height: 20,
            marginLeft: 12,
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
        },
        emptyText: {
            fontSize: 14,
            color: '#666',
            textAlign: 'center',
        },
        loadingContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
        },
        // Mobile-matching styles
        listTitle: {
            marginBottom: normalizeSize(16),
            color: primary,
            fontSize: fontSizeLarge,
        },
        renderSectionHeaderSelected: {
            borderTopLeftRadius: normalizeSize(containerBorderRadius),
            borderTopRightRadius: normalizeSize(containerBorderRadius),
            paddingTop: 16,
            paddingLeft: 12,
            paddingRight: 12,
            width: '100%',
            backgroundColor: '#DFF4FF',
        },
        renderSectionHeader: {
            display: 'flex',
            flexDirection: 'column-reverse',
        },
        sectionHeaderTitleSelected: {
            alignSelf: 'center',
            textAlign: 'center',
        },
        selectedGiftAction: {
            justifyContent: 'space-around',
            display: 'flex',
            flexDirection: 'row',
            marginLeft: normalizeSize(-8),
            marginRight: normalizeSize(-8),
            backgroundColor: 'white',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        selectedHeader: {
            justifyContent: 'center',
        },
        selectedDivider: {
            marginTop: 16,
            marginBottom: 16,
            height: 1,
            backgroundColor: '#D3D4D7',
        },
        selectedDividerSelected: {
            marginTop: 12,
            backgroundColor: '#21409a',
            opacity: 0.8,
            marginLeft: 12,
            marginRight: 12,
            height: 2,
            marginBottom: 0,
        },
        selectedTitleContainer: {
            alignSelf: 'center',
            textAlign: 'center',
            marginTop: 18,
            marginBottom: 8,
        },
        selectected: {
            marginBottom: normalizeSize(16),
            paddingLeft: normalizeSize(14),
            paddingRight: normalizeSize(14),
            paddingBottom: normalizeSize(12),
            backgroundColor: secondary10,
            borderRadius: normalizeSize(12),
        },
        renderItem: {
            paddingBottom: normalizeSize(16),
        },
        bottomSheetInjectContent: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
        },
        bottomSheetGiftFlastList: {
            flex: 1,
        },
        titleSelectedSection: {
            color: '#21409a',
            fontWeight: '700',
            lineHeight: 'normal',
        },
        titleRemainingSection: {
            color: '#21409a',
            fontWeight: '400',
            lineHeight: 'normal',
        },
    };
};
